/**
 * Numeric semver comparison for the app-update gate.
 *
 * Compares two dotted version strings ("1.0.56") segment-by-segment as numbers,
 * NOT as strings — `"1.0.56" < "1.0.100"` is false under string comparison but
 * true here, which is what a store-version gate needs. Missing trailing segments
 * are treated as 0 ("1.2" === "1.2.0"). Non-numeric segments are treated as 0.
 */

const SEGMENT_SPLIT = /[.\-+]/;

const toSegments = (version: string): number[] =>
  version
    .trim()
    .split(SEGMENT_SPLIT)
    .map((part) => {
      const n = parseInt(part, 10);
      return Number.isFinite(n) ? n : 0;
    });

/**
 * Returns -1 when `a` is older than `b`, 1 when `a` is newer, 0 when equal.
 */
export const compareVersions = (a: string, b: string): -1 | 0 | 1 => {
  const aSegments = toSegments(a);
  const bSegments = toSegments(b);
  const length = Math.max(aSegments.length, bSegments.length);

  for (let i = 0; i < length; i += 1) {
    const aValue = aSegments[i] ?? 0;
    const bValue = bSegments[i] ?? 0;
    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
  }

  return 0;
};

/** True when `current` is strictly older than `target` (i.e. needs to update). */
export const isVersionBelow = (current: string, target: string): boolean =>
  compareVersions(current, target) < 0;
